import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';

import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { mutedInk, stepColor } from '../cbos/palette';
import { useBrandBase } from '../cbos/useBrandBase';
import {
    ProcessStep,
    ProcessStepKey,
    ProcessType,
    ProcessTypeKey,
    processTypes,
    stepTitle,
} from './processModel';

/**
 * The vertical process rail: the process type on top, below it the three steps
 * the property runs through, drawn as chevron bands that point downwards into
 * each other. Each band is clickable and switches the perspectives shown in the
 * middle column.
 */

interface Properties {
    type: ProcessType;
    selectedStep: ProcessStepKey;
    onSelectType: (type: ProcessTypeKey) => void;
    onSelectStep: (step: ProcessStepKey) => void;
}

const WIDTH = 188;
const X0 = 4;
const X1 = WIDTH - 4;
const CENTER_X = WIDTH / 2;
const BAND_HEIGHT = 78;
const BAND_GAP = 9;
const NOTCH = 12;
const TOP = 4;
const LABEL_X = 60;
const LINE_HEIGHT = 16;

const bandTop = (index: number) => TOP + index * (BAND_HEIGHT + BAND_GAP);

const bandPath = (index: number): string => {
    const top = bandTop(index);
    const bottom = top + BAND_HEIGHT;
    const topEdge = index === 0 ? `M ${X0},${top} L ${X1},${top}` : `M ${X0},${top} L ${CENTER_X},${top + NOTCH} L ${X1},${top}`;
    return `${topEdge} L ${X1},${bottom} L ${CENTER_X},${bottom + NOTCH} L ${X0},${bottom} Z`;
};

/**
 * SVG text does not wrap and does not clip, so a long step label is broken into at most two lines
 * on word boundaries and whatever still does not fit is cut with an ellipsis. Without the cut the
 * text simply runs out of the chevron and across the map — SVG has no `text-overflow`.
 *
 * Labels are translated, so neither the split nor the cut can be hard-coded.
 */
const wrapLabel = (label: string, maxCharacters = 13): string[] => {
    const words = label.split(/\s+/).filter(Boolean);
    const lines: string[] = [];

    words.forEach((word) => {
        const current = lines[lines.length - 1];
        if (current && `${current} ${word}`.length <= maxCharacters) {
            lines[lines.length - 1] = `${current} ${word}`;
        } else {
            lines.push(word);
        }
    });

    const clip = (line: string): string =>
        line.length <= maxCharacters ? line : `${line.slice(0, maxCharacters - 1).trimEnd()}…`;

    if (lines.length <= 2) {
        return (lines.length > 0 ? lines : [label]).map(clip);
    }

    // Everything past the first line is folded into the second and then cut, so a long title loses
    // its tail rather than its middle.
    return [clip(lines[0]), clip(lines.slice(1).join(' '))];
};

export default function ProcessStepRail({ type, selectedStep, onSelectType, onSelectStep }: Properties) {
    const brand = useBrandBase();
    const { t, i18n } = useTranslation();
    const typeFieldId = useId();
    const steps = type.steps;
    const railRef = useRef<HTMLDivElement | null>(null);
    const activeRef = useRef<SVGGElement | null>(null);

    /**
     * Den gewählten Schritt in den Blick holen.
     *
     * Bei dreißig Bändern liegt der gewählte oft außerhalb — nach einem Neuladen mit `?step=` in
     * der Adresse, oder wenn ein Typwechsel auf den ersten zurücksetzt. Eine Auswahl, die man
     * nicht sieht, ist keine.
     *
     * `nearest`, damit nichts rollt, was ohnehin sichtbar ist: Ein Rail, der bei jedem Klick
     * springt, ist unruhiger als einer, der stehen bleibt.
     */
    useEffect(() => {
        // Der Aufruf selbst wahlweise: In der Testumgebung gibt es ihn nicht, und daran soll der
        // Rail nicht scheitern — er zeichnet auch ohne ihn richtig, er rollt nur nicht.
        activeRef.current?.scrollIntoView?.({ block: 'nearest' });
    }, [selectedStep, type.key]);
    const height = TOP + steps.length * (BAND_HEIGHT + BAND_GAP) + NOTCH;

    const colorOf = (_step: ProcessStep, index: number): string => stepColor(index, brand);

    return (
        /**
         * Höhe begrenzt, Inhalt scrollt.
         *
         * Mit drei Schritten war die Höhe kein Thema. Die Projektentwicklung hat dreißig, und die
         * Bänder wachsen mit ihrer Zahl — ungebremst zöge die Spalte die ganze Seite in die Länge,
         * und alles daneben stünde neben einer Leiste, die niemand ganz sieht. Die Karte reicht
         * deshalb höchstens bis zum unteren Fensterrand; was nicht hineinpasst, rollt.
         */
        <Card className="flex max-h-[calc(100vh-1.5rem)] flex-col xl:sticky xl:top-0">
            <div className="flex min-h-[52px] items-center border-b px-3 py-2">
                <span className="text-xs font-bold uppercase tracking-[0.09em] text-muted-foreground">
                    {t('portfolioDashboard.rail.title')}
                </span>
            </div>
            <div className="flex min-h-0 flex-1 flex-col px-2 pb-3 pt-2.5">
                <div className="mb-3 flex shrink-0 flex-col gap-1.5">
                    <Label htmlFor={typeFieldId}>{t('portfolioDashboard.types.label')}</Label>
                    <Select
                        value={type.key}
                        onValueChange={(value) => onSelectType(value as ProcessTypeKey)}
                    >
                        <SelectTrigger id={typeFieldId}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {processTypes.map((processType) => (
                                <SelectItem key={processType.key} value={processType.key}>
                                    {t(processType.labelKey)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {/* Eigener Bildlauf für die Bänder: Die Auswahl des Prozesstyps darüber bleibt
                    stehen, gerollt wird nur die Liste. Sonst müsste man zum Wechseln des Typs erst
                    nach oben zurück. */}
                <div ref={railRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                <svg
                    width="100%"
                    viewBox={`0 0 ${WIDTH} ${height}`}
                    role="tablist"
                    aria-label={t('portfolioDashboard.rail.title')}
                    className="block overflow-visible"
                >
                    <defs>
                        {steps.map((step, index) => {
                            const color = colorOf(step, index);
                            return (
                                <linearGradient key={step.key} id={`portfolioStep-${step.key}`} x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor={tinycolor(color).lighten(8).toHexString()} />
                                    <stop offset="100%" stopColor={tinycolor(color).darken(6).toHexString()} />
                                </linearGradient>
                            );
                        })}
                        <filter id="portfolioStepShadow" x="-30%" y="-30%" width="160%" height="180%">
                            <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="rgba(0,29,52,0.32)" />
                        </filter>
                    </defs>

                    {steps.map((step, index) => {
                        const color = colorOf(step, index);
                        const active = step.key === selectedStep;
                        const top = bandTop(index);
                        const middleY = top + NOTCH / 2 + BAND_HEIGHT / 2;
                        // Über `stepTitle`, nicht über den Übersetzungsschlüssel: Die Phasen der
                        // Projektentwicklung tragen ihre Beschriftung aus dem Modell.
                        const lines = wrapLabel(stepTitle(step, t, i18n.language));
                        const blockHeight = lines.length * LINE_HEIGHT + 14;
                        const firstLineY = middleY - blockHeight / 2 + LINE_HEIGHT - 3;

                        return (
                            // No focus rectangle around the chevron: the browser draws it around the
                            // group's bounding box, not the shape, which reads as a stray blue frame.
                            // Keyboard focus thickens the band's own outline instead.
                            <g
                                key={step.key}
                                ref={active ? activeRef : undefined}
                                className="cursor-pointer transition-transform hover:translate-x-0.5 focus:outline-none focus-visible:outline-none [&:focus-visible_path]:[stroke-width:3]"
                                role="tab"
                                tabIndex={0}
                                aria-selected={active}
                                onClick={() => onSelectStep(step.key)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onSelectStep(step.key);
                                    }
                                }}
                            >
                                <path
                                    className="transition-[fill,stroke,filter] duration-200"
                                    d={bandPath(index)}
                                    fill={active ? `url(#portfolioStep-${step.key})` : '#ffffff'}
                                    stroke={active ? 'none' : tinycolor(color).setAlpha(0.35).toRgbString()}
                                    strokeWidth={active ? 0 : 1.5}
                                    filter={active ? 'url(#portfolioStepShadow)' : undefined}
                                />
                                <circle
                                    cx={32}
                                    cy={middleY}
                                    r={13}
                                    fill={active ? 'rgba(255,255,255,0.22)' : tinycolor(color).setAlpha(0.12).toRgbString()}
                                    stroke={active ? 'rgba(255,255,255,0.65)' : 'none'}
                                    strokeWidth={active ? 1 : 0}
                                />
                                <text
                                    className="pointer-events-none select-none text-xs font-bold"
                                    x={32}
                                    y={middleY}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill={active ? '#ffffff' : color}
                                >
                                    {index + 1}
                                </text>
                                {lines.map((line, lineIndex) => (
                                    <text
                                        key={line}
                                        className="pointer-events-none select-none text-sm font-bold"
                                        x={LABEL_X}
                                        y={firstLineY + lineIndex * LINE_HEIGHT}
                                        fill={active ? '#ffffff' : brand}
                                    >
                                        {line}
                                    </text>
                                ))}
                                <text
                                    className="pointer-events-none select-none text-[11px] tracking-wide"
                                    x={LABEL_X}
                                    y={firstLineY + lines.length * LINE_HEIGHT + 4}
                                    fill={active ? 'rgba(255,255,255,0.78)' : mutedInk(brand)}
                                >
                                    {t('portfolioDashboard.rail.perspectiveCount', { count: step.perspectives.length })}
                                </text>
                            </g>
                        );
                    })}
                </svg>
                </div>
            </div>
        </Card>
    );
}
